/**
 * Máscara e busca automática de CEP. Ponto único de consulta de CEP do app —
 * a busca vai pelo proxy do backend (ver `fetchAddressByCep`).
 */

import { fetchPostalCode } from './api/fetch-postal-code'

/** Formata para `99999-999` (limita a 8 dígitos). */
export function formatCEP(value: string): string {
    const numbers = value.replace(/\D/g, '').slice(0, 8)
    return numbers.replace(/^(\d{5})(\d)/, '$1-$2')
}

/** Só os dígitos do CEP. */
export function unMaskCEP(value: string): string {
    return value.replace(/\D/g, '')
}

export interface CepAddress {
    street?: string
    neighborhood?: string
    city?: string
    state?: string
    /** Código IBGE do município. */
    ibge?: string
}

/**
 * Busca o endereço pelo CEP. Devolve `null` quando o CEP é inválido (≠ 8
 * dígitos), não existe ou a requisição falha — o caller mantém o que já estava
 * preenchido, e a digitação nunca trava por causa de uma consulta.
 *
 * Vai pelo proxy do backend (`utils/postal-code/:cep`) e não direto na ViaCEP:
 * o navegador deixa de depender do CORS e do limite de taxa de um serviço
 * externo, e a troca de provedor passa a ser mudança de servidor, sem deploy do
 * front. Este era o caminho já usado pela maioria das telas; a chamada direta
 * sobrevivia só em três.
 */
export async function fetchAddressByCep(cep: string): Promise<CepAddress | null> {
    const clean = unMaskCEP(cep)
    if (clean.length !== 8) return null
    try {
        const data = await fetchPostalCode(clean)
        if (!data || data.erro) return null
        return {
            street: data.logradouro || undefined,
            neighborhood: data.bairro || undefined,
            city: data.localidade || undefined,
            state: data.uf || undefined,
            ibge: data.ibge || undefined,
        }
    } catch {
        // `fetchPostalCode` lança quando o CEP não existe; aqui isso é só
        // "não achei", não um erro a propagar.
        return null
    }
}
